/**
 * BannersCommunity — full-bleed gradient banner on the community profile
 * settings page (`/profile/edit`) that deep-links the profile owner to the
 * playlist-management view in their library.
 *
 * Pure presentational component: no Apollo, no Redux. i18n via
 * `connectStaticText({ storeKey: 'bannersCommunity' })` (same pattern as
 * `ProfileReviewBanner`). `language` is passed as a prop from the parent page
 * to keep it in sync with the rest of the settings tree (see
 * `lib/components/Community/CLAUDE.md` rule 4).
 *
 * Responsive layout:
 *  - < 768px: column layout — __top row (44px icon + title/body text) above a
 *    full-width stretched CTA button.
 *  - ≥ 768px: row layout — __top (94px icon + title/body text) + flex spacer +
 *    auto-width CTA. Text is visible at ALL breakpoints; only the icon size
 *    changes (small icon hidden at 768px+, large icon shown).
 */

import React, { memo } from 'react'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import Link, { TARGET_BLANK } from '../../../../Link/index'
import CommunityStonePath from '../../../../Icon/icons/CommunityStonePath/index'
import ExternalLink from '../../../../Icon/icons/ExternalLink/index'

const BLOCK = 'banners-community'
const MANAGE_PLAYLISTS_ROUTE = '/playlist/default'

export interface BannersCommunityProps {
  language: string
  staticText: InternationalStrings
}

function BannersCommunityContent () {
  const buttonLabel = useTranslatedText({ keys: ['managePlaylists'] })
  const title = useTranslatedText({ keys: ['showExploring'] })
  const body = useTranslatedText({ keys: ['playlistsDescription'] })

  return (
    <div className={BLOCK}>
      {/* Top section: icon + title/body text — visible at all breakpoints.
          Icon swaps: 44px (mobile) hidden at 768px+; 94px (desktop) shown at 768px+. */}
      <div className={`${BLOCK}__top`}>
        <CommunityStonePath
          size="44px"
          color="#FFFFFF"
          className={`${BLOCK}__icon`}
        />
        <CommunityStonePath
          size="94px"
          color="#FFFFFF"
          className={`${BLOCK}__icon-desktop`}
        />
        <div className={`${BLOCK}__text`}>
          <span className={`${BLOCK}__title`} style={{ color: '#ffffff' }}>{title}</span>
          <span className={`${BLOCK}__body`} style={{ color: '#ffffff' }}>{body}</span>
        </div>
      </div>
      <div className={`${BLOCK}__spacer`} />
      <Link
        to={MANAGE_PLAYLISTS_ROUTE}
        target={TARGET_BLANK}
        rel="noopener noreferrer"
        directLink
        className={`${BLOCK}__button`}
        style={{ color: '#ffffff' }}
      >
        <span className={`${BLOCK}__button-label`}>{buttonLabel}</span>
        <ExternalLink
          size="16px"
          color="#FFFFFF"
          className={`${BLOCK}__button-icon`}
        />
      </Link>
    </div>
  )
}

function BannersCommunity ({ staticText }: BannersCommunityProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <BannersCommunityContent />
    </StaticTextProvider>
  )
}

const MemoizedBannersCommunity = memo(BannersCommunity)

export default connectStaticText({ storeKey: 'bannersCommunity' })(MemoizedBannersCommunity)
